import { Glue42 } from "../../glue";
import { Glue42Notification } from "./notification";
import { GnsNotificationSettings, GnsNotificationActionSettings, GnsNotificationActionPayload, NotificationsConfiguration, StreamMessage, StreamNotification, StreamNotificationDeltas } from "./types";
import callbackRegistry, { Callback, CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import generate from "shortid";
import { PanelAPI } from "./panel";

const STARTING_INDEX = 0;

interface NotificationEventMapExt extends NotificationEventMap {
    "action": Event;
    "unknown": Event;
}

interface GnsNotificationEventArgs {
    event: keyof NotificationEventMapExt;
    notificationId?: string;
    notificationActionPayload?: GnsNotificationActionPayload;
    error?: Error;
}

export class Notifications {
    private _panel: PanelAPI;

    private readonly NotificationsSubscribeStream = "T42.GNS.Subscribe.Notifications";
    private readonly NotificationsCounterStream = "T42.Notifications.Counter";
    private readonly RaiseNotificationMethodName = "T42.GNS.Publish.RaiseNotification";
    private readonly NotificationsExecuteMethod = "T42.Notifications.Execute";

    private methodsRegistered = false;
    private NOTIFICATIONS_CONFIGURE_METHOD_NAME = "T42.Notifications.Configure";
    private readonly methodNameRoot = "T42.Notifications.Handler-" + generate();
    private nextId = 0;
    private notifications: { [id: string]: Glue42Notification } = {};
    private registry: CallbackRegistry = callbackRegistry();
    private subscriptionForNotifications: Glue42.Interop.Subscription;
    private subscriptionForCounter: Glue42.Interop.Subscription;
    private subscribedForNotifications = false;
    private subscribedCounterStream = false;
    private subscriptionsCountForNotifications = 0;
    private subscriptionsCountForCounter = 0;
    private logger: Glue42.Logger.API;

    constructor(private interop: Glue42.Interop.API, logger: Glue42.Logger.API) {
        this.logger = logger.subLogger("notifications");
        this._panel = new PanelAPI(interop, this.onStreamEventCore.bind(this));
        this.subscribeInternalEvents();
    }

    public get maxActions(): number {
        return 2;
    }

    public get panel() {
        return this._panel.toAPI();
    }

    public async raise(options: Glue42.Notifications.Glue42NotificationOptions): Promise<Glue42.Notifications.Glue42Notification> {
        this.validate(options);

        if (!this.methodsRegistered) {
            const bunchOfPromises = [];
            for (let index = STARTING_INDEX; index < this.maxActions; index++) {
                bunchOfPromises.push(
                    this.interop.register(`${this.methodNameRoot}_${index}`, this.handleNotificationEvent.bind(this))
                );
            }

            this.methodsRegistered = true;
            await Promise.all(bunchOfPromises);
        }

        // transform actions
        const id = String(this.nextId++);
        const type = options.type ?? "Notification";
        const notification: GnsNotificationSettings = {
            id: options.id,
            state: options.state ?? "Active",
            title: options.title,
            type,
            severity: options.severity ?? "None",
            description: options.body,
            glueRoutingDetailMethodName: `${this.methodNameRoot}_${STARTING_INDEX}`,
            actions: [],
            sourceId: id,
            source: options.source,
            publishExtraEvents: true, // such as onerror, onshow and onclose
        };

        if (options.actions) {
            this.handleActions(options, id, notification);
        }

        this.handleOptions(options, notification);

        const g42notification = new Glue42Notification(options);
        this.notifications[id] = g42notification;

        try {
            const invocationResult = await this.interop.invoke(this.RaiseNotificationMethodName, { notification });
            g42notification.id = invocationResult.returned?.id;
        } catch (err) {
            // let user code to subscribe for the onerror event
            const errorMessage = err.message;
            setTimeout(() => {
                this.handleNotificationErrorEvent(g42notification, errorMessage);
            }, 1);
        }
        return g42notification;
    }

    public async setFilter(filter: Glue42.Notifications.NotificationFilter): Promise<Glue42.Notifications.NotificationFilter> {
        const result = await this.interop.invoke("T42.Notifications.Filter", filter);
        return result.returned;
    }

    public async getFilter(): Promise<Glue42.Notifications.NotificationFilter> {
        const result = await this.interop.invoke("T42.Notifications.Filter");
        return result.returned;
    }

    public async configure(options: Glue42.Notifications.Configuration): Promise<void> {
        if (!options || Array.isArray(options)) {
            throw new Error("Invalid options - should be an object.");
        }

        if (Object.values(options).length === 0) {
            throw new Error("The argument must be a non-empty object.");
        }

        if (typeof options.enable !== "undefined" && typeof options.enable !== "boolean") {
            throw new Error("Expected type of enabled - boolean.");
        }

        if (typeof options.enableToasts !== "undefined" && typeof options.enableToasts !== "boolean") {
            throw new Error("Expected type of enableToasts - boolean.");
        }

        if (typeof options.toastExpiry !== "undefined" && typeof options.toastExpiry !== "number") {
            throw new Error("Expected type of toastExpiry - number.");
        }

        if (options.sourceFilter && typeof options.sourceFilter !== "object") {
            throw new Error("Expected type of sourceFilter - object.");
        }

        if (options.sourceFilter?.allowed && !Array.isArray(options.sourceFilter?.allowed)) {
            throw new Error("Expected type of sourceFilter.allowed - array.");
        }

        if (options.sourceFilter?.blocked && !Array.isArray(options.sourceFilter?.blocked)) {
            throw new Error("Expected type of sourceFilter.blocked - array.");
        }

        if (options.toasts && typeof options.toasts !== "object") {
            throw new Error("Expected type of (options.toasts - object.");
        }

        if (options.toasts?.mode && typeof options.toasts.mode !== "string") {
            throw new Error("Expected type of (options.toasts.mode - string.");
        }

        if (options.toasts?.stackBy && typeof options.toasts.stackBy !== "string") {
            throw new Error("Expected type of (options.toasts.stackBy - string.");
        }


        const result = await this.interop.invoke(this.NOTIFICATIONS_CONFIGURE_METHOD_NAME, options);
        return result.returned;
    }

    public async getConfiguration(): Promise<Glue42.Notifications.Configuration> {
        const result = await this.interop.invoke(this.NotificationsExecuteMethod, { command: "getConfiguration" });
        return result.returned;
    }

    public async list(): Promise<Glue42.Notifications.NotificationData[]> {
        const interopResult = await this.interop.invoke<{ notifications: Glue42.Notifications.NotificationData[] }>(this.NotificationsExecuteMethod, { command: "list" });
        return interopResult.returned.notifications;
    }

    public async updateData(id: string, data: unknown) {
        const replacer = (key: string, value: unknown) =>
            typeof value === "undefined" ? null : value;
        const attribute = {
            key: "data",
            value: {
                stringValue: JSON.stringify(data, replacer)
            }
        }
        const interopResult = await this.interop.invoke(this.NotificationsExecuteMethod, { command: "create-or-update-attribute", data: { id, attribute } });
        return interopResult.returned;
    }

    public onRaised(callback: (notification: Glue42.Notifications.NotificationData) => void): UnsubscribeFunction {
        return this.onStreamEventCore("on-notification-raised", callback);
    }

    public onStateChanged(callback: (id: string, state: Glue42.Notifications.State) => any): UnsubscribeFunction {
        return this.onStreamEventCore("on-state-changed", callback);
    }

    public onClosed(callback: (notification: { id: string }) => void): UnsubscribeFunction {
        return this.onStreamEventCore("on-notification-closed", callback);
    }

    public onConfigurationChanged(callback: (config: NotificationsConfiguration) => void): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("Please provide the callback as a function!");
        }
        this.subscribe();
        const un = this.registry.add("on-configuration-changed", callback);
        return () => {
            un();
            this.closeStreamSubscriptionIfNoNeeded();
        };
    }

    public onCounterChanged(callback: (info: { count: number }) => void): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("Please provide the callback as a function!");
        }
        this.subscribeForCounterStream();
        const un = this.registry.add("on-counter-changed", callback);
        return () => {
            un();
            this.closeStreamCounterSubscriptionIfNoNeeded();
        };
    }

    public onDataChanged<T>(callback: (data: T) => void): UnsubscribeFunction {
        if (typeof callback !== "function") {
            throw new Error("Please provide the callback as a function!");
        }
        this.subscribe();
        const un = this.registry.add("on-notification-data-changed", callback);
        return () => {
            un();
            this.closeStreamSubscriptionIfNoNeeded();
        };
    }

    public async clearAll(): Promise<void> {
        await this.interop.invoke(this.NotificationsExecuteMethod, { command: "clearAll" });
    }

    public async clearOld(): Promise<void> {
        await this.interop.invoke(this.NotificationsExecuteMethod, { command: "clearAllOld" });
    }

    public async clear(id: string): Promise<void> {
        if (!id) {
            throw new Error("The 'id' argument cannot be null or undefined");
        }
        if (typeof (id) !== "string") {
            throw new Error("The 'id' argument must be a string");
        }
        await this.interop.invoke(this.NotificationsExecuteMethod, { command: "clear", data: { id } });
    }

    public async click(id: string, action?: string): Promise<void> {
        await this.interop.invoke(this.NotificationsExecuteMethod, { command: "click", data: { id, action } });
    }

    public async setState(id: string, state: "Active" | "Acknowledged" | "Stale"): Promise<void> {
        if (!id) {
            throw new Error("The 'id' argument cannot be null or undefined");
        }
        if (typeof (id) !== "string") {
            throw new Error("The 'id' argument must be a string");
        }

        if (!state) {
            throw new Error("The 'state' argument cannot be null or undefined");
        }

        this.validateState(state);

        await this.interop.invoke(this.NotificationsExecuteMethod, { command: "updateState", data: { id, state } });
    }

    public toAPI(): Glue42.Notifications.API {
        return {
            maxActions: this.maxActions,
            panel: this._panel.toAPI(),
            raise: this.raise.bind(this),
            setFilter: this.setFilter.bind(this),
            getFilter: this.getFilter.bind(this),
            configure: this.configure.bind(this),
            getConfiguration: this.getConfiguration.bind(this),
            list: this.list.bind(this),
            onRaised: this.onRaised.bind(this),
            onStateChanged: this.onStateChanged.bind(this),
            onClosed: this.onClosed.bind(this),
            onConfigurationChanged: this.onConfigurationChanged.bind(this),
            onCounterChanged: this.onCounterChanged.bind(this),
            onDataChanged: this.onDataChanged.bind(this),
            clearAll: this.clearAll.bind(this),
            clearOld: this.clearOld.bind(this),
            clear: this.clear.bind(this),
            click: this.click.bind(this),
            setState: this.setState.bind(this),
            updateData: this.updateData.bind(this),
        };
    }

    private onStreamEventCore<T extends Callback>(key: string, callback: T) {
        if (typeof callback !== "function") {
            throw new Error("Please provide the callback as a function!");
        }
        this.subscribe();
        const un = this.registry.add(key, callback);
        return () => {
            un();
            this.closeStreamSubscriptionIfNoNeeded();
        };
    }

    private handleOptions(options: Glue42.Notifications.Glue42NotificationOptions, notification: GnsNotificationSettings) {
        if (options.icon) {
            notification.attributes = notification.attributes || [];
            notification.attributes.push(
                { key: "icon", value: { stringValue: options.icon } }
            );
        }

        if (options.data) {
            notification.attributes = notification.attributes || [];
            const dataAsString = JSON.stringify(options.data);
            notification.attributes.push(
                { key: "data", value: { stringValue: dataAsString } }
            );
        }

        if (typeof options.panelExpiry === "number") {
            notification.attributes = notification.attributes || [];
            notification.attributes.push(
                { key: "panelExpiry", value: { stringValue: options.panelExpiry.toString() } }
            );
        }

        if (typeof options.toastExpiry === "number") {
            notification.attributes = notification.attributes || [];
            notification.attributes.push(
                { key: "toastExpiry", value: { stringValue: options.toastExpiry.toString() } }
            );
        }
    }

    private handleActions(options: Glue42.Notifications.Glue42NotificationOptions, id: string, notification: GnsNotificationSettings) {
        const validActions = options.actions.slice(0, this.maxActions);

        let index = STARTING_INDEX;
        for (const action of validActions) {
            const args: GnsNotificationActionPayload & { [key: string]: string; } = {
                g42notificationId: id,
                g42action: action.action,
                g42interopMethod: action.interop?.method,
                g42interopTarget: action.interop?.target,
            };
            // properly convert between args and GNS value
            const parameters = Object.keys(args).map((key) => {
                const value = args[key];
                return {
                    name: key,
                    value: {
                        stringValue: value
                    }
                };
            });

            const glueAction: GnsNotificationActionSettings = {
                name: `${this.methodNameRoot}_${index}`,
                description: action.title,
                displayName: action.title,
                parameters
            };

            notification.actions.push(glueAction);
            index++;
        }
    }

    private validate(options: Glue42.Notifications.Glue42NotificationOptions) {
        if (!options) {
            throw new Error("invalid options - should be an object");
        }
        if (typeof options !== "object") {
            throw new Error("invalid options - should be an object");
        }
        if (!options.title) {
            throw new Error("invalid options - should have title");
        }
        if (typeof options.title !== "string") {
            throw new Error("invalid options - title should be a string");
        }
        if (options.severity && typeof options.severity !== "string") {
            throw new Error("invalid options - severity should be a string")
        }
        if (options.panelExpiry && typeof options.panelExpiry !== "number") {
            throw new Error("invalid options - panelExpiry should be a number")
        }
        if (options.toastExpiry && typeof options.toastExpiry !== "number") {
            throw new Error("invalid options - toastExpiry should be a number")
        }
        if (options.state) {
            this.validateState(options.state);
        }
    }

    private validateState(state: string) {
        if (typeof (state) !== "string") {
            throw new Error("The 'state' argument must be a string");
        }

        const validStates = [
            "Active",
            "Acknowledged",
            "Stale"
        ];

        if (!validStates.includes(state)) {
            throw new Error(`The state argument: ${state} is not valid!`);
        }
    }

    private subscribe() {
        this.subscriptionsCountForNotifications++;
        if (!this.subscribedForNotifications) {
            this.subscribedForNotifications = true;

            this.logger.info(`Attempting to subscribe to "${this.NotificationsSubscribeStream}".`);
            this.interop
                .subscribe(this.NotificationsSubscribeStream, {
                    arguments: {
                        sendDeltaOnly: true
                    }
                })
                .then((sub) => {
                    this.subscriptionForNotifications = sub;
                    this.logger.info(`Successfully subscribed to "${this.NotificationsSubscribeStream}".`);
                    sub.onData(({ data }) => {
                        this.handleData(data);
                    });
                    sub.onClosed((...args) => {
                        this.subscribedForNotifications = false;
                        this.logger.info(
                            `Stream subscription Closed - ${JSON.stringify(args)}`
                        );
                    });
                    sub.onFailed((...args) => {
                        this.subscribedForNotifications = false;
                        this.logger.warn(
                            `Stream subscription Failed - ${JSON.stringify(args)}`
                        );
                    });
                })
                .catch((e) => {
                    this.subscribedForNotifications = false;
                    this.logger.error(`Unable to subscribe to "${this.NotificationsSubscribeStream}"`, e);
                });
        }
    }

    private subscribeForCounterStream() {
        this.subscriptionsCountForCounter++;
        if (!this.subscribedCounterStream) {
            this.subscribedCounterStream = true;
            this.logger.info(`Attempting to subscribe to "${this.NotificationsCounterStream}".`);
            this.interop
                .subscribe(this.NotificationsCounterStream, {
                    arguments: {
                        sendDeltaOnly: true
                    }
                })
                .then((sub) => {
                    this.subscriptionForCounter = sub;
                    this.logger.info(`Successfully subscribed to "${this.NotificationsCounterStream}".`);
                    sub.onData(({ data }) => {
                        this.registry.execute("on-counter-changed", { count: data.count });
                    });
                    sub.onClosed((...args) => {
                        this.subscribedCounterStream = false;
                        this.logger.info(
                            `Stream subscription Closed - ${JSON.stringify(args)}`
                        );
                    });
                    sub.onFailed((...args) => {
                        this.subscribedCounterStream = false;
                        this.logger.warn(
                            `Stream subscription Failed - ${JSON.stringify(args)}`
                        );
                    });
                })
                .catch((e) => {
                    this.subscribedCounterStream = false;
                    this.logger.error(`Unable to subscribe to "${this.NotificationsCounterStream}"`, e);
                });
        }
    }

    private subscribeInternalEvents() {
        this.registry.add("on-notification-closed", (id) => {
            this.handleOnClosed(id);
        });
        this.registry.add("on-notification-raised", (notification) => {
            this.handleOnShow(notification.id);
        });
    }

    private handleData(message: StreamMessage) {
        try {
            if ("items" in message && Array.isArray(message.items)) {
                this.handleItemsData(message);
            } else if ("deltas" in message && Array.isArray(message.deltas)) {
                this.handleDeltas(message);
            }
            if ("configuration" in message && typeof message.configuration === "object") {
                this.logger.info(`Received configuration ${JSON.stringify(message.configuration)} from the stream`);
                this.registry.execute("on-configuration-changed", message.configuration, message.configuration.allApplications);
            }
            if ("command" in message && typeof message.command === "string") {
                this.logger.info(`Received command "${message.command ?? JSON.stringify(message)}" from the stream`);
                if (message.command === "showPanel" || message.command === "hidePanel") {
                    this.registry.execute("on-panel-visibility-changed", message.command === "showPanel");
                }
            }
        } catch (e) {
            this.logger.error(`Failed to parse data from the stream`, e);
        }
    }

    private handleItemsData(message: StreamNotification) {
        const items = message.items;
        this.logger.info(
            `Received ${items.length} notifications from the stream`
        );
        const notifications: Glue42.Notifications.NotificationData[] = items;

        // will handle the notifications in case of snapshot
        if (message.isSnapshot) {
            notifications.forEach((n) => {
                this.registry.execute("on-notification-raised", n);
            });
        } else {
            const notification = notifications[0];
            if (notification.state === "Closed") {
                this.registry.execute("on-notification-closed", { id: notification.id });
            } else {
                this.registry.execute("on-notification-raised", notification);
            }
        }
    }

    private handleDeltas(message: StreamNotificationDeltas) {
        const deltas = message.deltas;
        deltas.forEach((info) => {
            const id = info.id;
            const delta = info.delta ?? {};
            if (delta.state === "Closed") {
                this.registry.execute("on-notification-closed", { id, ...delta });
            } else if (delta.state) {
                this.registry.execute("on-state-changed", { id }, delta.state);
            } else if (delta.attributes) {
                const attributes = delta.attributes;
                const dataAttribute = attributes.find((a) => a.key === "data");
                if (dataAttribute) {
                    this.registry.execute("on-notification-data-changed", { id }, JSON.parse(dataAttribute.value.stringValue));
                }
            }
        });
    }

    private handleOnClosed(id: string) {
        const { notification, key }: { notification: Glue42Notification; key: string; } = this.getNotification(id);
        if (notification) {
            this.handleEvent(notification, "close");
            delete this.notifications[key];
        }
    }

    private handleOnShow(id: string) {
        const { notification }: { notification: Glue42Notification; key: string; } = this.getNotification(id);
        if (notification) {
            this.handleEvent(notification, "show");
        }
    }

    private getNotification(id: string) {
        let notification: Glue42Notification | undefined;
        let key: string;
        for (const k in this.notifications) {
            if (this.notifications[k].id === id) {
                notification = this.notifications[k];
                key = k;
                break;
            }
        }
        return { notification, key };
    }

    private handleNotificationEvent(args: Readonly<any>): void {
        const gnsNotificationArgs = this.getGnsNotificationArgs(args);
        if (gnsNotificationArgs.event === "unknown") {
            // we cannot handle this event since we do not understand it
            return;
        }
        const notification = this.notifications[gnsNotificationArgs.notificationId];
        if (!notification) {
            // we cannot handle this event since notification is missing
            return;
        }
        this.handleNotificationEventCore(notification, gnsNotificationArgs);
    }

    private handleNotificationEventCore(notification: Readonly<Glue42Notification>, args: Readonly<GnsNotificationEventArgs>): void {
        switch (args.event) {
            case "action": {
                return this.handleNotificationActionEvent(notification, args.notificationActionPayload);
            }
            case "click": {
                return this.handleNotificationClickEvent(notification);
            }
            case "close": {
                return this.handleEvent(notification, "close");
            }
            case "error": {
                return this.handleNotificationErrorEvent(notification, args.error);
            }
            case "show": {
                return this.handleEvent(notification, "show");
            }
            default: {
                // unexpected event, do nothing
            }
        }
    }

    private handleNotificationActionEvent(notification: Readonly<Glue42Notification>, payload: Readonly<GnsNotificationActionPayload>): void {
        // user event
        const event: { type: string, action?: string } = {
            type: "onaction",
            action: payload.g42action
        };

        if (notification.onaction) {
            notification.onaction(event as Glue42.Notifications.ActionEvent);
        }

        // interop
        const actionDefinition = (notification.actions || []).find((item) => item.action === payload.g42action);
        const actionInterop = actionDefinition.interop;
        if (actionInterop) {
            this.interop.invoke(actionInterop.method, actionInterop.arguments || {}, actionInterop.target || "best");
        }

        // dispatch
        notification.dispatchEvent(event as Event);
    }

    private handleNotificationClickEvent(notification: Readonly<Glue42Notification>): void {
        // user event
        const event = { type: "onclick" } as Event;
        if (notification.onclick) {
            notification.onclick(event);
        }

        // interop call
        const clickInterop = notification.options.clickInterop;
        if (clickInterop) {
            this.interop.invoke(clickInterop.method, clickInterop.arguments || {}, clickInterop.target || "best");
        }

        // dispatch
        notification.dispatchEvent(event);
    }

    private handleEvent(notification: Readonly<Glue42Notification>, eventType: keyof NotificationEventMap): void {
        // user event
        const event = { type: eventType } as Event;
        const eventName = `on${eventType}`;
        notification[eventName]?.(event);

        // dispatch
        notification.dispatchEvent(event);
    }

    private handleNotificationErrorEvent(notification: Readonly<Glue42Notification>, error: Readonly<Error>): void {
        // user event
        const event = { type: "onerror", error } as unknown as Event;
        if (notification.onerror) {
            notification.onerror(event);
        }

        // dispatch
        notification.dispatchEvent(event);
    }

    private getGnsNotificationArgs(args: Readonly<any>): GnsNotificationEventArgs {
        let result: GnsNotificationEventArgs;
        const event = args.notification?.event;
        if (!event) {
            result = this.getBackwardGnsNotificationArgs(args);
        } else {
            result = {
                event,
                notificationId: args.notification.sourceNotificationId,
                notificationActionPayload: args as GnsNotificationActionPayload
            };
        }
        return result;
    }

    private getBackwardGnsNotificationArgs(args: Readonly<any>): GnsNotificationEventArgs {
        let result: GnsNotificationEventArgs;
        if (args.g42notificationId) {
            result = {
                event: "action",
                notificationId: args.g42notificationId,
                notificationActionPayload: args as GnsNotificationActionPayload
            };
        } else if (args.notification?.sourceNotificationId) {
            result = {
                event: "click",
                notificationId: args.notification.sourceNotificationId,
                notificationActionPayload: args as GnsNotificationActionPayload
            };
        } else {
            result = {
                event: "unknown",
                notificationId: undefined,
                notificationActionPayload: args as GnsNotificationActionPayload
            };
        }

        return result;
    }

    private closeStreamSubscriptionIfNoNeeded(): void {
        this.subscriptionsCountForNotifications--;

        if (this.subscriptionForNotifications && this.subscriptionsCountForNotifications === 0) {
            this.subscriptionForNotifications.close();
            this.subscriptionForNotifications = undefined;
        }
    }

    private closeStreamCounterSubscriptionIfNoNeeded(): void {
        this.subscriptionsCountForCounter--;

        if (this.subscriptionForCounter && this.subscriptionsCountForCounter === 0) {
            this.subscriptionForCounter.close();
            this.subscriptionForCounter = undefined;
        }
    }
}
